
import { useState, useEffect } from "react";
import { Copy, Share2, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useIsMobile } from "@/hooks/use-mobile";

interface ShareBoardDialogProps {
  boardId: string;
  boardName: string;
}

export function ShareBoardDialog({ boardId, boardName }: ShareBoardDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  const [boardSlug, setBoardSlug] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const isMobile = useIsMobile();

  const checkPublicStatus = async () => {
    try {
      const { data, error } = await supabase
        .from('kanban_boards')
        .select('is_public, name, client_id, clients(name)')
        .eq('id', boardId)
        .single();

      if (error) throw error;

      setIsPublic(data.is_public || false);
      
      // Generate slug from client name or board name
      const clientName = data.clients?.name;
      const slugSource = clientName || data.name;
      const generatedSlug = slugSource
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // Remove accents
        .replace(/[^a-z0-9]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-|-$/g, '');
      
      setBoardSlug(generatedSlug);
    } catch (error) {
      console.error('Error checking public status:', error);
    }
  };

  const generatePublicLink = async () => {
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('kanban_boards')
        .update({ is_public: true })
        .eq('id', boardId);

      if (error) throw error;

      setIsPublic(true);
      toast.success("Link público gerado com sucesso!");
    } catch (error) {
      console.error('Error generating public link:', error);
      toast.error("Erro ao gerar link público");
    } finally {
      setIsLoading(false);
    }
  };

  const disablePublicAccess = async () => {
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('kanban_boards')
        .update({ is_public: false })
        .eq('id', boardId);

      if (error) throw error;

      setIsPublic(false);
      toast.success("Acesso público desabilitado");
    } catch (error) {
      console.error('Error disabling public access:', error);
      toast.error("Erro ao desabilitar acesso público");
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = async () => {
    if (!boardSlug) return;

    const publicUrl = `${window.location.origin}/board/client/${boardSlug}`;
    
    try {
      await navigator.clipboard.writeText(publicUrl);
      toast.success("Link copiado para a área de transferência!");
    } catch (error) {
      // Fallback para navegadores mais antigos
      const textArea = document.createElement('textarea');
      textArea.value = publicUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success("Link copiado para a área de transferência!");
    }
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      checkPublicStatus();
    }
  };

  const publicUrl = boardSlug ? `${window.location.origin}/board/client/${boardSlug}` : '';

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size={isMobile ? "sm" : "default"}>
          <Share2 className="w-4 h-4 mr-2" />
          Compartilhar
        </Button>
      </DialogTrigger>
      <DialogContent className={isMobile ? "w-[95vw] max-w-[95vw]" : "max-w-md"}>
        <DialogHeader>
          <DialogTitle>Compartilhar Quadro</DialogTitle>
          <DialogDescription>
            Gere um link público para que qualquer pessoa possa acessar e editar este quadro.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm font-medium">Acesso Público</Label>
              <p className="text-xs text-muted-foreground">
                Permite que qualquer pessoa com o link acesse o quadro
              </p>
            </div>
            <Button
              variant={isPublic ? "destructive" : "default"}
              size="sm"
              onClick={isPublic ? disablePublicAccess : generatePublicLink}
              disabled={isLoading}
            >
              {isPublic ? (
                <>
                  <EyeOff className="w-4 h-4 mr-2" />
                  Desabilitar
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4 mr-2" />
                  Habilitar
                </>
              )}
            </Button>
          </div>

          {isPublic && boardSlug && (
            <div className="space-y-2">
              <Label htmlFor="public-link">Link Público</Label>
              <div className="flex gap-2">
                <Input
                  id="public-link"
                  value={publicUrl}
                  readOnly
                  className="flex-1"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyToClipboard}
                  className="flex-shrink-0"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Qualquer pessoa com este link poderá visualizar e editar o quadro em tempo real.
              </p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
